import { FragmentEntity, FragmentForUpdate, RawFragment } from './Types';
import { GalleryEntity } from '../Gallery/Types';

import { GalleryModel } from '../Gallery/GalleryModel';

import { ItemTypeEntity } from '../ItemType/Types';
import { BaseFragmentModel } from '../BaseFragment/BaseFragmentModel';
import { QuestionEntity, RawQuestion } from '../Question/Types';
import { ExtractQuestions } from '@modules/Steps/Shared/Enums/ExtractQuestions';
import { QuestionModel } from '../Question/QuestionModel';
import { ItemTypeModel } from '../ItemType/ItemTypes';

export class FragmentModel extends BaseFragmentModel implements FragmentEntity {
    createdAt: string = new Date().toISOString();
    updatedAt: string = new Date().toISOString();
    deletedAt: string | null = null;
    varMapping: string | null = null;
    fragments: FragmentEntity[] = [];
    questions: QuestionEntity[] = [];
    frozen: boolean = false;
    publish: boolean = false;
    qty?: number | null = null;
    slug?: string | null;
    galleries?: GalleryEntity[] = [];
    itemTypes?: ItemTypeEntity[] = [];

    constructor(fragment?: RawFragment) {
        super(fragment);
        if (fragment) {
            this.initialize(fragment);
        }
    }

    initialize(fragment: RawFragment) {
        super.initialize(fragment);
        this.createdAt = fragment.created_at;
        this.updatedAt = fragment.updated_at;
        this.deletedAt = fragment.deleted_at;
        this.varMapping = fragment.data.var_mapping;
        this.questions = ExtractQuestions(fragment.data).map(
            ([_, value]: [string, RawQuestion]) => new QuestionModel(value)
        );
        this.frozen = fragment.frozen;
        this.publish = fragment.publish;
        this.qty = fragment.qty;
        this.slug = fragment.slug;
        this.itemTypes = this.collectAllItemTypes(fragment);
        this.galleries = fragment.galleries?.length
            ? fragment.galleries.map(g => new GalleryModel(g))
            : [];
    }

    parse(): RawFragment {
        return {
            ...super.parse(),
            created_at: this.createdAt,
            updated_at: this.updatedAt,
            deleted_at: this.deletedAt,
            data: {
                ...this.questions.reduce(
                    (acc, q, idx) => ({ ...acc, [idx]: q.parse() }),
                    {}
                ),
                var_mapping: this.varMapping,
                fragments: this.fragments?.map(f => f.parse()),
                item_types: this.itemTypes?.map(item => item.parse()),
            },
            frozen: this.frozen,
            publish: this.publish,
            qty: this.qty,
            slug: this.slug,
            galleries: this.galleries?.map(g => g.parse()),
        };
    }

    transformFragmentForUpdate(): FragmentForUpdate {
        return {
            fragment_id: this.id,
            qty: this.qty,
            questions: this.questions.map(q => q.transformQuestionForUpdate()),
        };
    }

    private collectAllItemTypes(fragment: RawFragment): ItemTypeModel[] {
        const currentItemTypes = fragment.data.item_types?.length
            ? fragment.data.item_types.map(item => new ItemTypeModel(item))
            : [];

        const nestedItemTypes = fragment.data.fragments?.length
            ? fragment.data.fragments.flatMap(nestedFragment =>
                  this.collectAllItemTypes(nestedFragment)
              )
            : [];

        return [...currentItemTypes, ...nestedItemTypes];
    }
}
